import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = ["onMouseenter"]
const _hoisted_3 = { key: 0 }

import { computed, ref } from 'vue'
import ChartTooltip from '../atoms/ChartTooltip.vue'
import ColorLegendContinuous from '@/design-system/atoms/ColorLegendContinuous.vue'
import { ColorScaleInterpolate, getColorForValue } from '@/utils/colorScale'
import TableBase from '../atoms/TableBase.vue'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'
import TextBase from '../atoms/TextBase.vue'
import { useHoverStore } from '@/pinia/hover'
// Original Props definition removed due to duplication

type HeaderLabel = {
  title: string
  subtitle?: string
}

// Type for the data structure passed via the matrix prop
// (Matches ProcessedHeatmapData in CardHeatmapNew.vue)
type HeatmapTableData = {
  axes: { x: (string | null)[]; y: (string | null)[] }
  matrix: (number | null)[][]
  summary: { x: (number | null)[]; y: (number | null)[] }
  tooltipLabelsMatrix: string[][] // Expect pre-computed labels
}

type HoveredCellData = {
  value: number | null
  color: string
  x: number // Mouse X
  y: number // Mouse Y
  dynamicLabel: string // Label comes from tooltipLabelsMatrix
}

type Props = {
  matrix?: HeatmapTableData // Expect the full processed data object here
  xSumLabel?: string
  ySumLabel?: string
  // tooltipLabel prop is removed
  fullscreen?: boolean
  colorScale: ColorScaleInterpolate
  dataFormat?: FormatOptions // For cell values and summaries
  xAxisFormat?: FormatOptions // For X-axis labels
  yAxisFormat?: FormatOptions // For Y-axis labels
  start: number
  end: number
}

type EmittedCellData = {
  value: number | null
  color: string | undefined
  rowIndex: number
  colIndex: number // This is the paginated index
  // other potential properties from TableBase's cell object if needed
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HeatmapTableNew',
  props: {
    matrix: {},
    xSumLabel: {},
    ySumLabel: {},
    fullscreen: { type: Boolean },
    colorScale: {},
    dataFormat: {},
    xAxisFormat: {},
    yAxisFormat: {},
    start: {},
    end: {}
  },
  setup(__props: any) {

const { locale } = useI18n()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()

// Adjust Prop type to expect the new structure in 'matrix'
const props = __props

const table = computed(() =>
  props.matrix
    ? {
        body: props.matrix.matrix.map(
          (
            row,
            rowIndex // Get rowIndex here
          ) =>
            paginate(row).map((value, colIndex) => {
              // Get paginated colIndex here
              const color =
                value !== undefined && value !== null
                  ? getColorForValue(props.colorScale, value).toHex()
                  : '#fff'
              return {
                value,
                color,
                style: { backgroundColor: color },
                class: 'custom-cell',
                rowIndex: rowIndex, // Add rowIndex
                colIndex: colIndex // Add paginated colIndex
              }
            })
        ),
        headers: {
          top: paginate(props.matrix.axes.x).map((label) => ({
            label: {
              title: label ? formatter(label, locale.value, props.xAxisFormat) : '-'
            } as HeaderLabel,
            class: 'label-x'
          })),
          left: props.matrix.axes.y.map((id) => ({
            label: {
              title: id ? formatter(id, locale.value, props.yAxisFormat) : '-'
            } as HeaderLabel,
            id,
            class: 'label-y'
          })),
          bottomLeft: { label: { title: props.xSumLabel } as HeaderLabel, class: 'label-x' },
          bottom:
            props.ySumLabel !== undefined
              ? paginate(props.matrix.summary.x).map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined,
          topRight:
            props.xSumLabel !== undefined
              ? { label: { title: props.ySumLabel } as HeaderLabel, class: 'label-x' }
              : undefined,
          right:
            props.xSumLabel !== undefined
              ? props.matrix.summary.y.map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined
        }
      }
    : undefined
)

const hoveredCell = ref<HoveredCellData | undefined>()

// Define the type for the cell data passed by TableBase, now including our added indices
const mouseEnterCell = (
  // TableBase emits (cell, centerX, centerY)
  // Our 'cell' now contains value, color, rowIndex, colIndex, style, class
  cell: EmittedCellData,
  centerX: number,
  centerY: number
) => {
  const { value, color, rowIndex, colIndex } = cell

  if (value === null || color === undefined || !props.matrix) {
    hoveredCell.value = undefined // Clear tooltip if no value/color
    return
  }

  // Get the pre-computed label from the matrix data using the correct indices
  // We use the paginated colIndex directly as the tooltipLabelsMatrix should correspond to the filtered+paginated view
  const dynamicLabel = props.matrix?.tooltipLabelsMatrix?.[rowIndex]?.[colIndex] ?? '?'

  hoveredCell.value = {
    value,
    color,
    x: centerX,
    y: centerY,
    dynamicLabel // Use the pre-computed label
  }
}

const mouseLeaveCell = () => {
  hoveredCell.value = undefined
}

function paginate<T>(array: T[]): T[] {
  return array.slice(Math.max(0, props.start), Math.min(array.length, props.end))
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (hoveredCell.value)
        ? (_openBlock(), _createBlock(ChartTooltip, {
            key: 0,
            label: hoveredCell.value.dynamicLabel,
            color: hoveredCell.value.color,
            value: 
          hoveredCell.value.value !== undefined && hoveredCell.value.value !== null
            ? _unref(formatter)(hoveredCell.value.value, _unref(locale), _ctx.dataFormat)
            : '-'
        ,
            x: hoveredCell.value.x,
            y: hoveredCell.value.y
          }, null, 8, ["label", "color", "value", "x", "y"]))
        : _createCommentVNode("", true)
    ])),
    (table.value && table.value.body.length > 0)
      ? (_openBlock(), _createBlock(TableBase, {
          key: 0,
          table: table.value,
          sortDirection: 'asc',
          bodyHeight: "400px",
          leftWidth: "85px",
          layout: "fixed",
          rightTextAlign: "center",
          caption: "Heatmap table",
          onMouseEnterCell: mouseEnterCell,
          onMouseLeaveCell: mouseLeaveCell
        }, {
          "header-cell": _withCtx(({ cell, position }) => [
            (position !== 'left')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (cell.label)
                    ? (_openBlock(), _createBlock(TextBase, {
                        key: 0,
                        text: cell.label.title,
                        type: position === 'right' || position === 'bottom' ? 'label' : 'tiny'
                      }, null, 8, ["text", "type"]))
                    : _createCommentVNode("", true)
                ], 64))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  style: { cursor: 'pointer' },
                  onMouseenter: () => _unref(addHoveredItem)(cell['id'], { color: '#fadb14' }),
                  onMouseleave: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(removeLastHoveredItem) && _unref(removeLastHoveredItem)(...args)))
                }, [
                  (cell.label)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createVNode(TextBase, {
                          text: cell.label.title,
                          type: "tiny",
                          overflow: "ellipsis"
                        }, null, 8, ["text"])
                      ]))
                    : _createCommentVNode("", true)
                ], 40, _hoisted_2))
          ]),
          footer: _withCtx(() => [
            _createVNode(ColorLegendContinuous, {
              formatOptions: _ctx.dataFormat,
              colorScale: _ctx.colorScale
            }, null, 8, ["formatOptions", "colorScale"])
          ]),
          _: 1
        }, 8, ["table"]))
      : _createCommentVNode("", true)
  ]))
}
}

})